// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

/* This file provides MSAL auth configuration to get access token through nested app authentication. */

/* global console */

import { PublicClientNext, type IPublicClientApplication } from "@azure/msal-browser";

export { AccountManager };

export const PERMISSION = [
  "user.read",
  "openid",
  "profile",
  "Calendars.Read.Shared"
  // "Directory.Read.All",
]
// const applicationId = "498fb21b-5156-47d9-8d98-41605315c5be";
const applicationId = window.CONFIG?.clientId || "b7ec9375-2552-4bbc-8505-351e03b49ff1";
const tenantId = window.CONFIG?.tenantId || "1be5b0cb-0e02-4548-be48-57b7a5c71228";

const msalConfig = {
  auth: {
    clientId: applicationId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    supportsNestedAppAuth: true,
  },
};

// Encapsulate functions for getting user account and token information.
class AccountManager {
  loginHint: string = "";
  pca: IPublicClientApplication | undefined = undefined;
  isInitializing: boolean = false;
  initializationPromise: Promise<void> | null = null;

  // Initialize MSAL public client application.
  async initialize(loginHint: string) {
    // Nếu đã khởi tạo rồi thì không cần khởi tạo lại
    if (this.pca !== undefined) {
      console.log("AccountManager already initialized");
      return;
    }

    // Nếu đang trong quá trình khởi tạo, trả về promise đang chạy
    if (this.isInitializing && this.initializationPromise) {
      console.log("AccountManager initialization in progress, waiting...");
      return this.initializationPromise;
    }

    // Bắt đầu khởi tạo
    this.isInitializing = true;

    this.initializationPromise = new Promise<void>(async (resolve, reject) => {
      try {
        console.log("Initializing AccountManager with MSAL...");
        this.loginHint = loginHint;
        this.pca = await PublicClientNext.createPublicClientApplication(msalConfig);
        console.log("AccountManager initialized successfully");
        this.isInitializing = false;
        resolve();
      } catch (error) {
        console.error("Failed to initialize AccountManager:", error);
        this.isInitializing = false;
        this.initializationPromise = null;
        reject(error);
      }
    });

    return this.initializationPromise;
  }

  /**
   * 
   * @param scopes the minimum scopes needed.
   * @returns An access token.
   */
  async ssoGetToken(scopes: string[] = ['user.read']) {
    const userAccount = await this.ssoGetUserIdentity(scopes);
    return userAccount.accessToken;
  }

  /**
   * 
   * Uses MSAL and nested app authentication to get the user account from Office SSO.
   * This demonstrates how to work with user identity from the token.
   * 
   * @param scopes The minimum scopes needed.
   * @returns The user account data (including identity).
   */
  async ssoGetUserIdentity(scopes: string[]) {
    // Kiểm tra và đợi khởi tạo nếu cần
    if (this.pca === undefined) {
      if (this.isInitializing && this.initializationPromise) {
        console.log("Waiting for AccountManager initialization to complete...");
        await this.initializationPromise;
      } else {
        throw new Error("AccountManager is not initialized! Please call initialize() first.");
      }
    }

    // Đảm bảo this.pca đã được khởi tạo sau khi chờ
    if (this.pca === undefined) {
      throw new Error("AccountManager initialization failed");
    }

    // Specify minimum scopes needed for the access token.
    const tokenRequest = {
      scopes: scopes,
      loginHint: this.loginHint,
    };

    try {
      console.log("Trying to acquire token silently...");
      const loginRequest = {
        scopes: ['https://api.powerplatform.com/.default'],
        redirectUri: window.location.origin,
      }
      //acquireTokenSilent requires an active account. Check if one exists, otherwise use ssoSilent.
      const account = this.pca.getActiveAccount();

      const userAccount = account ? await this.pca.acquireTokenSilent({
        ...tokenRequest,
        ...loginRequest,
      }) : await this.pca.ssoSilent(tokenRequest);

      console.log("Acquired token silently.");
      return userAccount;
    } catch (error) {
      console.log(`Unable to acquire token silently: ${error}`);
    }

    // Đảm bảo this.pca vẫn được khởi tạo
    if (this.pca === undefined) {
      throw new Error("AccountManager has lost initialization");
    }

    // Acquire token silent failure. Send an interactive request via popup.
    try {
      console.log("Trying to acquire token interactively...");
      const userAccount = await this.pca.acquireTokenPopup(tokenRequest);
      console.log("Acquired token interactively.");
      return userAccount;
    } catch (popupError) {
      // Acquire token interactive failure.
      console.log(`Unable to acquire token interactively: ${popupError}`);
      throw new Error(`Unable to acquire access token: ${popupError}`);
    }
  }
}
